import { useFormikContext } from "formik";
import React, { FC, useState } from "react";
import { useLocale } from "../../contexts/localeContext";
import { TableContextProvider } from "../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { WizardStepLayout } from '../wizard/wizard-summary-layout';
import { Lookup } from "../../models/Lookup";
import { SearchTerm } from "../../models/search/SearchTerm";
import { TableColumn } from "../../models/table/TableColumn";
import TableTab from "../tabs/table-tab";
import { ASSIGNMENT_LIMITS } from "@/constants/system-constants.ts";
import { toast } from "react-toastify";
import ActivityFrame from "../activity-frame/activity-frame";

interface AssignStepProps {
    onNext: (e: any) => void;
    setTotalSelected: React.Dispatch<React.SetStateAction<number>>;
    tableColumns: TableColumn[];
    searchColumns: SearchTerm[];
    dataMethod: (listParameter?: any) => Promise<any>;
    title: string;
    lookup?: Lookup[];
    selectedIds: string[];
    selectedRows: any[];
    setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>;
    setSelectedRows: React.Dispatch<React.SetStateAction<string[]>>;
    originalSelectedIds: string[];
    busy: boolean;
    alwaysSelectedAndDisabled?: string[]
}

const AssignStep: FC<AssignStepProps> = ({ tableColumns, searchColumns, onNext, setTotalSelected, dataMethod, title, selectedIds, selectedRows, setSelectedIds, setSelectedRows, lookup, originalSelectedIds, alwaysSelectedAndDisabled, busy }) => {
    const { setFieldValue } = useFormikContext<any>();
    const { translate } = useLocale();

    const getTotalSelected = (newSelectedIds: string[]): number => {
        newSelectedIds = alwaysSelectedAndDisabled ? newSelectedIds.filter((id) => !alwaysSelectedAndDisabled.includes(id)) : newSelectedIds;
        originalSelectedIds = alwaysSelectedAndDisabled ? originalSelectedIds.filter((id) => !alwaysSelectedAndDisabled.includes(id)) : originalSelectedIds;

        const toBeUnassigned = originalSelectedIds.filter((id) => !newSelectedIds.includes(id));
        const toBeAssigned = newSelectedIds.filter((id) => !originalSelectedIds.includes(id));
        return toBeAssigned.length + toBeUnassigned.length;
    }

    // This method is here to make sure that there cannot be more than a 100 records selected between the assigned and unassigned
    // as this can cause issues between Primo and the database connection.
    // TODO: Any parent component using this component should be updated to use the batch assign and unassign service before removing this
    //       example of this batching can be found in AccessGroupService and EnrolmentService.
    const updateSelectedIds = (newSelectedIds: string[]) => {
        newSelectedIds = alwaysSelectedAndDisabled ? newSelectedIds.filter((id) => !alwaysSelectedAndDisabled.includes(id)) : newSelectedIds;
        if (getTotalSelected(newSelectedIds) > ASSIGNMENT_LIMITS.MAX_ASSIGNMENTS_PER_USER) {
            toast.error(translate.byKey(TranslationKeys.aMaximumOf100RecordsCanBeSelected));
            return;
        }
        setSelectedIds(newSelectedIds);
        setTotalSelected(getTotalSelected(newSelectedIds));
    }

    const onLocalNext = (e: any) => {
        if (getTotalSelected(selectedIds) > 0) {
            onNext(e);
            setFieldValue("selectedIds", selectedIds);
            setFieldValue("selectedRows", selectedRows);
        }
    };

    return <WizardStepLayout onNext={onLocalNext} nextDisabled={getTotalSelected(selectedIds) === 0}>
        <ActivityFrame busy={busy} busyMessage={translate.byKey(TranslationKeys.pleaseWaitWhileLoading)}>
            <div className="overflow-y-auto" style={{ height: '87%' }}>
                <TableContextProvider
                    externalBusy={busy}
                    dataMethod={dataMethod}
                    tableDescriptor={tableColumns}
                    searchDescriptor={searchColumns}
                    useTable
                    usePagination
                    useFilter
                    useSearch
                    useSort
                    lookup={lookup}
                    alwaysSelectedAndDisabled={alwaysSelectedAndDisabled}>
                    <TableTab
                        cyName="assignStep_tbl"
                        title={title}
                        selectRows={true}
                        selectedIds={selectedIds}
                        selectedRows={selectedRows}
                        setSelectedIds={(selectedIds) => { updateSelectedIds(selectedIds) }}
                        setSelectedRows={setSelectedRows} />
                </TableContextProvider>
            </div>
        </ActivityFrame>
    </WizardStepLayout>
}

export default AssignStep;
